import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, linkedSignal, output, signal, viewChild } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { concatMap, tap, timer } from 'rxjs';
import { marked } from 'marked';
import { MdQueryApiService, mdQueryApiBaseUrl, mdQueryError } from './md-query-api.service';
import { NoteRequest, SearchMode, SearchResponse, SearchResultItem, SubmittedSearch } from './md-query.models';
import { jkGlb } from 'jk-service';

@Component({
  selector: 'mdq-search', standalone: true, imports: [DecimalPipe, FormsModule, RouterLink],
  templateUrl: './mdq-search.component.html', styleUrl: './md-query.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MdqSearchComponent {
  private readonly api = inject(MdQueryApiService);
  readonly apiBaseUrl = input<string>('');
  readonly apiToken = input('');
  readonly searchCompleted = output<SearchResponse>();
  readonly connection = computed(() => {
    const baseUrl = this.apiBaseUrl().trim();
    const token = this.apiToken().trim();
    console.log('connection computed', { baseUrl, token });
    return baseUrl && token ? { baseUrl, token } : undefined;
  });
  readonly queryText = signal('');
  readonly searchMode = signal<SearchMode>('query');
  readonly selectedCollection = signal('');
  readonly pathPrefix = signal('');
  readonly selectedTags = signal<string[]>([]);
  readonly tagText = signal('');
  readonly resultLimit = signal(20);
  readonly feedback = signal('');
  readonly submitted = linkedSignal({ source: this.connection, computation: (): SubmittedSearch | undefined => undefined });
  readonly collectionsResource = rxResource({
    params: this.connection,
    stream: ({ params }) => timer(1000).pipe(concatMap(() => this.api.getCollections(params))),
  });
  readonly collections = computed(() => this.collectionsResource.hasValue() ? this.collectionsResource.value().collections : []);
  readonly searchResource = rxResource({
    params: () => {
      const request = this.submitted();
      const connection = this.connection();
      return request && connection ? { connection, request } : undefined;
    },
    stream: ({ params }) => this.api.search(params.connection, params.request.mode, params.request.payload).pipe(tap(response => this.searchCompleted.emit(response))),
  });
  readonly isSearching = this.searchResource.isLoading;
  readonly results = computed(() => this.searchResource.hasValue() ? this.searchResource.value().results : []);
  readonly tookMs = computed(() => this.searchResource.hasValue() ? this.searchResource.value().tookMs : 0);
  readonly hasResults = computed(() => this.results().length > 0);
  readonly errorMessage = computed(() => {
    if (!this.connection()) return null;
    const error = this.searchResource.error();
    return error ? mdQueryError(error) : null;
  });
  readonly collectionsError = computed(() => {
    if (!this.connection()) return null;
    const error = this.collectionsResource.error();
    return error ? mdQueryError(error) : null;
  });
  readonly previewRequest = linkedSignal({ source: this.submitted, computation: (): NoteRequest | undefined => undefined });
  readonly activeResult = computed(() => this.previewRequest()?.item ?? null);
  readonly isDrawerOpen = computed(() => !!this.previewRequest());
  readonly showSource = signal(false);
  readonly previewDialog = viewChild.required<ElementRef<HTMLDialogElement>>('previewDialog');
  readonly previewResource = rxResource({
    params: () => {
      const request = this.previewRequest();
      const connection = this.connection();
      return request && connection ? { connection, request } : undefined;
    },
    stream: ({ params }) => {
      const { item, range } = params.request;
      const startLine = range ? Math.max(1, item.startLine - 3) : undefined;
      const endLine = range ? item.endLine + 3 : undefined;
      return this.api.getNote(params.connection, item.collection, item.filePath, startLine, endLine);
    },
  });
  readonly previewContent = computed(() => this.previewResource.hasValue() ? this.previewResource.value() : null);
  readonly isPreviewLoading = this.previewResource.isLoading;
  readonly previewError = computed(() => {
    if (!this.connection()) return null;
    const error = this.previewResource.error();
    return error ? mdQueryError(error) : null;
  });
  // Angular sanitizes the resulting HTML at the innerHTML binding.
  readonly previewHtml = computed(() => marked.parse(this.previewContent()?.content ?? '', { async: false }));
  readonly previewStartLine = computed(() => {
    const req = this.previewRequest();
    if (req?.range && req.item) {
      return Math.max(1, req.item.startLine - 3);
    }
    return 1;
  });
  readonly previewLines = computed(() => (this.previewContent()?.content ?? '').split('\n').map((text, index) => ({ text, number: this.previewStartLine() + index })));

  constructor() {
    this.api
    effect(() => {
      const open = this.isDrawerOpen();
      const dialog = this.previewDialog().nativeElement;
      if (open && !dialog.open) dialog.showModal();
      else if (!open && dialog.open) dialog.close();
    });
  }
  refreshCollections(): void {
    if (this.connection()) {
      this.collectionsResource.reload();
    }
  }
  addTags(): void {
    const tags = this.tagText().split(/[,，\s]+/).map(tag => tag.replace(/^#/, '').trim()).filter(Boolean);
    this.selectedTags.update(current => [...new Set([...current, ...tags])]); this.tagText.set('');
  }
  removeTag(tag: string): void { this.selectedTags.update(tags => tags.filter(value => value !== tag)); }
  search(): void {
    if (!this.connection() || !this.queryText().trim()) return;
    this.addTags(); this.feedback.set('');
    this.submitted.set({ mode: this.searchMode(), payload: {
      query: this.queryText().trim(), limit: this.resultLimit(),
      ...(this.selectedCollection() ? { collection: this.selectedCollection() } : {}),
      ...(this.pathPrefix().trim() ? { pathPrefix: this.pathPrefix().trim() } : {}),
      ...(this.selectedTags().length ? { tags: this.selectedTags() } : {}),
    } });
  }
  openPreview(item: SearchResultItem, range: boolean): void {
    if (!this.connection()) return;
    this.showSource.set(range);
    this.previewRequest.set({ item, range });
  }
  closePreview(): void { this.previewRequest.set(undefined); }
  switchPreview(range: boolean): void { const item = this.activeResult(); if (item) this.openPreview(item, range); }
  isHit(line: number): boolean { const item = this.activeResult(); return !!item && line >= item.startLine && line <= item.endLine; }
  async copyLink(item: SearchResultItem): Promise<void> {
    const connection = this.connection();
    if (!connection) return;
    const params = new URLSearchParams({ collection: item.collection, path: item.filePath, startLine: String(item.startLine), endLine: String(item.endLine) });
    const url = `${mdQueryApiBaseUrl(connection.baseUrl)}/notes?${params}`;
    const title = item.title.replace(/[\[\]\r\n]/g, ' ');
    await this.copy(`[${title} L${item.startLine}-${item.endLine}](${url})`);
  }
  async copyContent(): Promise<void> { const content = this.previewContent(); if (content) await this.copy(content.content); }
  downloadNote(): void {
    const content = this.previewContent();
    const item = this.activeResult();
    if (!content) return;
    const blob = new Blob([content.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    let filename = item?.filePath ? item.filePath.split('/').pop() || '' : '';
    if (!filename) filename = `${content.title || 'note'}.md`;
    if (!filename.endsWith('.md')) filename += '.md';
    if (this.previewRequest()?.range && item) {
      const extIdx = filename.lastIndexOf('.');
      const suffix = `_L${Math.max(1, item.startLine - 3)}-L${item.endLine + 3}`;
      filename = extIdx > 0 ? `${filename.slice(0, extIdx)}${suffix}.md` : `${filename}${suffix}.md`;
    }
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    this.feedback.set(`已下載檔案：${filename}`);
  }
  private async copy(value: string): Promise<void> {
    try { await navigator.clipboard.writeText(value); this.feedback.set('已複製到剪貼簿'); }
    catch { this.feedback.set('無法寫入剪貼簿，請確認瀏覽器權限及安全連線。'); }
  }
}
